// The big bundle sizes are ok here, since the mock server is not used in production.

import {
  type Collection,
  createServer,
  JSONAPISerializer,
  type ModelInstance,
} from 'miragejs'

import {
  type ExtendedHandlerOptions,
  useLazySeedingManager,
} from '@@/api/mock-server/lazy-seeding/index.js'

import {
  snakeCase as _snakeCase,
  camelCase as _camelCase,
  upperFirst as _upperFirst,
} from 'lodash-es'
import { mirageLog } from '@/utils/dev-log/index.js'
import { seed } from '@ngneat/falso'
import { inflections } from 'inflected'
import { getCurrentTimestamp } from '@/utils/date/getter.js'

import * as AccountNumber from './AccountNumber.js'
import * as ActivePermissionsList from './ActivePermissionsList.js'
import * as AI from './AI.js'
import * as AlternativePayer from './AlternativePayer.js'
import * as AlternativePrice from './AlternativePrice.js'
import * as Batch from './Batch.js'
import * as BillingNote from './BillingNote.js'
import * as BoardingEvent from './BoardingEvent.js'
import * as Breeds from './Breeds.js'
import * as Bundle from './Bundle.js'
import * as CalendarAppointment from './CalendarAppointment.js'
import * as CalendarUser from './CalendarUser.js'
import * as CancellationReason from './CancellationReason.js'
import * as CentralizedSetting from './CentralizedSetting.js'
import * as Client from './Client.js'
import * as CreditAllocation from './CreditAllocation.js'
import * as ClientCommunicationPreference from './ClientCommunicationPreference.js'
import * as ClientStatement from './ClientStatement.js'
import * as ClientStatementTotalBalance from './ClientStatementTotalBalance.js'
import * as ClinicStaffUsers from './ClinicStaffUsers.js'
import * as ComplaintType from './ComplaintType.js'
import * as ComplaintTypeGroup from './ComplaintTypeGroup.js'
import * as Consultation from './consultation/Consultation.js'
import * as ConsultationConversation from './consultation/ConsultationConversation.js'
import * as ConsultationDiagnosis from './consultation/ConsultationDiagnosis.js'
import * as ConsultationDischargeinstruction from './consultation/ConsultationDischargeinstruction.js'
import * as ConsultationFood from './consultation/ConsultationFood.js'
import * as ConsultationItemOverview from './consultation/ConsultationItemOverview.js'
import * as ConsultationItemSubgroup from './consultation/ConsultationItemSubgroup.js'
import * as ConsultationItemTemplate from './consultation/ConsultationItemTemplate.js'
import * as ConsultationLaboratoryAnalysis from './consultation/ConsultationLaboratoryAnalysis.js'
import * as ConsultationLaboratoryReferral from './consultation/ConsultationLaboratoryReferral.js'
import * as ConsultationMedicine from './consultation/ConsultationMedicine.js'
import * as ConsultationNote from './consultation/ConsultationNote.js'
import * as ConsultationNoteVersions from './consultation/ConsultationNoteVersions.js'
import * as ConsultationPreviousNotes from './consultation/ConsultationPreviousNotes.js'
import * as ConsultationProcedure from './consultation/ConsultationProcedure.js'
import * as ConsultationRefillOverview from './consultation/ConsultationRefillOverview.js'
import * as ConsultationSupply from './consultation/ConsultationSupply.js'
import * as ConsultationType from './consultation/ConsultationType.js'
import * as ConsultationReport from './reports/ConsultationReport.js'
import * as Conversation from './conversations/Conversation.js'
import * as ConversationMessage from './conversations/ConversationMessage.js'
import * as ConversationParticipant from './conversations/ConversationParticipant.js'
import * as CustomIntegration from './CustomIntegration.js'
import * as DayNote from './DayNote.js'
import * as Department from './Department.js'
import * as DepartmentGroup from './DepartmentGroup.js'
import * as DevPlaygroundPatientBilling from './DevPlaygroundPatientBilling.js'
import * as DevPlaygroundPatientBillingInvoice from './DevPlaygroundPatientBillingInvoice.js'
import * as Diagnosis from './Diagnosis.js'
import * as DischargeInstructionVersions from './DischargeInstructionVersions.js'
import * as Estimate from './Estimate.js'
import * as FeatureBlock from './FeatureBlock.js'
import * as FormAssignment from './FormAssignment.js'
import * as FormTemplate from './FormTemplate.js'
import * as GeneralPrescription from './GeneralPrescription.js'
import * as GeneralPrescriptionItem from './GeneralPrescriptionItem.js'
import * as HealthPlan from './HealthPlan.js'
import * as HomeDelivery from './HomeDelivery.js'
import * as HomeDeliveryIntegrations from './HomeDeliveryIntegrations.js'
import * as HomeDeliveryOrder from './HomeDeliveryOrder.js'
import * as HoldingPlaceNumber from './HoldingPlaceNumber.js'
import * as ImportFile from './ImportFile.js'
import * as ItalianMunicipality from './ItalianMunicipality.js'
import * as InsuranceClaim from './InsuranceClaim.js'
import * as Invoice from './Invoice.js'
import * as InvoiceEventLog from './InvoiceEventLog.js'
import * as InvoiceFinalization from './InvoiceFinalization.js'
import * as InvoicePayment from './InvoicePayment.js'
import * as InvoiceReport from './reports/InvoiceReport.js'
import * as InvoiceReportSavedFilter from './reports/SavedFilter.js'
import * as InvoiceRow from './InvoiceRow.js'
import * as InvoiceRuns from './billing/invoiceRuns.js'
import * as ItemCategory from './ItemCategory.js'
import * as CatalogItemList from './catalog/management/ItemList.js'
import * as CatalogItems from './catalog/Items.js'
import * as ItemDiagnosis from './catalog/management/ItemDiagnosis.js'
import * as ItemSubgroup from './catalog/management/ItemSubgroup.js'
import * as LaboratoryGroup from './catalog/management/LaboratoryGroup.js'
import * as CodelistDiagnoses from './codelist/Diagnoses.js'
import * as ListItem from './ListItem.js'
import * as Locum from './Locum.js'
import * as LocumAssignment from './LocumAssignment.js'
import * as MergedSettings from './MergedSettings.js'
import * as ConversationMessageMedia from './conversations/ConversationMessageMedia.js'
import * as Message from './conversations/Message.js'
import * as MessageReceipt from './conversations/MessageReceipt.js'
import * as MicrochipCheck from './MicrochipCheck.js'
import * as NavigationSearchResult from './NavigationSearchResult.js'
import * as ProvetPayPaymentReport from './reports/ProvetPayPaymentReport.js'
import * as Notification from './Notification.js'
import * as OnlineBookingSettings from './OnlineBookingSettings.js'
import * as Organization from './Organization.js'
import * as OrganizationItem from './OrganizationItem.js'
import * as Patient from './Patient.js'
import * as ProviderMedicine from './ProviderMedicine.js'
import * as PatientCPRStatusEntry from './PatientCPRStatusEntry.js'
import * as PatientHealthPlan from './PatientHealthPlan.js'
import * as PatientHealthPlanItem from './PatientHealthPlanItem.js'
import * as PatientLocation from './PatientLocation.js'
import * as PatientLocationAttribute from './PatientLocationAttribute.js'
import * as PatientLocationAvailableCapacity from './PatientLocationAvailableCapacity.js'
import * as PaymentGateway from './PaymentGateway.js'
import * as PaymentMethod from './PaymentMethod.js'
import * as PermissionGroup from './PermissionGroup.js'
import * as Phone from './Phone.js'
import * as Prepayment from './Prepayment.js'
import * as PreregistrationContactMethods from './preregistration/ContactMethods.js'
import * as PreregistrationOTP from './preregistration/OTP.js'
import * as Reminder from './Reminder.js'
import * as ReminderCreationRule from './ReminderCreationRule.js'
import * as ReminderTemplate from './ReminderTemplate.js'
import * as RefillItem from './consultation/RefillItem.js'
import * as ReportExport from './reports/ReportExport.js'
import * as RevisionLog from './RevisionLog.js'
import * as ScheduleReport from './reports/ScheduleReport.js'
import * as SchedulingEvent from './SchedulingEvent.js'
import * as SearchResult from './SearchResult.js'
import * as ShareReport from './reports/ShareReport.js'
import * as Shift from './Shift.js'
import * as SidebarSection from './SidebarSection.js'
import * as SimpleBundle from './SimpleBundle.js'
import * as SimpleConsultationItem from './SimpleConsultationItem.js'
import * as Species from './Species.js'
import * as Speechmatics from './Speechmatics.js'
import * as StockItem from './StockItem.js'
import * as StockLocation from './StockLocation.js'
import * as SummaryConsultation from './reports/SummaryConsultation.js'
import * as SummaryDuration from './reports/SummaryDuration.js'
import * as SummaryRevenue from './reports/SummaryRevenue.js'
import * as Support from './Support.js'
import * as Tag from './Tag.js'
import * as TagText from './TagText.js'
import * as Task from './Task.js'
import * as TaskRepeat from './TaskRepeat.js'
import * as TreatmentDocument from './consultation/TreatmentDocument.js'
import * as UIConfig from './UIConfig.js'
import * as UKPostcodeLookup from './UKPostcodeLookup.js'
import * as UKPostcodeResult from './UKPostcodeResult.js'
import * as UnallocatedPayment from './UnallocatedPayment.js'
import * as Upload from './Upload.js'
import * as UploadGroup from './UploadGroup.js'
import * as User from './User.js'
import * as UserFilter from './UserFilter.js'
import * as UserViewHistory from './UserViewHistory.js'
import * as Ward from './Ward.js'
import * as Weight from './Weight.js'
import * as Wholesaler from './Wholesaler.js'
import * as ZendeskSettings from './ZendeskSettings.js'

seed('33')

/**
 * Entities that have route handlers defined.
 * This is the central registry for all entities that need API endpoints.
 *
 * **When to add an entity here:**
 * - The entity has a `routes()` function that defines API endpoints
 * - You want lazy seeding to work automatically for this entity's routes
 *
 * **When NOT to add an entity here:**
 * - The entity only has models/factories but no API routes
 * - The entity is only used internally (no external API needed)
 */
export type EntityWithRoutes = keyof typeof entitiesWithRoutes
export const entitiesWithRoutes = {
  AccountNumber,
  ActivePermissionsList,
  AI,
  AlternativePayer,
  BillingNote,
  BoardingEvent,
  Breeds,
  CalendarAppointment,
  CalendarUser,
  CancellationReason,
  CentralizedSetting,
  Client,
  ClientCommunicationPreference,
  ClientStatement,
  ClientStatementTotalBalance,
  ClinicStaffUsers,
  ComplaintType,
  ComplaintTypeGroup,
  Consultation,
  ConsultationDiagnosis,
  ConsultationDischargeinstruction,
  ConsultationItemOverview,
  ConsultationLaboratoryAnalysis,
  ConsultationLaboratoryReferral,
  ConsultationNote,
  ConsultationNoteVersions,
  ConsultationPreviousNotes,
  ConsultationRefillOverview,
  ConsultationType,
  ConsultationFood,
  ConsultationMedicine,
  ConsultationProcedure,
  ConsultationReport,
  ConsultationSupply,
  Conversation,
  ConversationMessage,
  CreditAllocation,
  CustomIntegration,
  DayNote,
  Department,
  DevPlaygroundPatientBilling,
  DevPlaygroundPatientBillingInvoice,
  Diagnosis,
  DischargeInstructionVersions,
  Estimate,
  FormAssignment,
  FormTemplate,
  GeneralPrescription,
  HealthPlan,
  HoldingPlaceNumber,
  HomeDelivery,
  HomeDeliveryIntegrations,
  ImportFile,
  InsuranceClaim,
  Invoice,
  InvoicePayment,
  InvoiceReport,
  InvoiceReportSavedFilter,
  InvoiceRow,
  InvoiceRuns,
  ItalianMunicipality,
  CatalogItemList,
  ItemCategory,
  CatalogItems,
  ItemDiagnosis,
  ItemSubgroup,
  LaboratoryGroup,
  CodelistDiagnoses,
  ListItem,
  Locum,
  MergedSettings,
  MessageReceipt,
  NavigationSearchResult,
  ProvetPayPaymentReport,
  Notification,
  OnlineBookingSettings,
  Organization,
  OrganizationItem,
  Patient,
  ProviderMedicine,
  PatientCPRStatusEntry,
  PatientHealthPlan,
  PatientHealthPlanItem,
  PatientLocation,
  PatientLocationAttribute,
  PatientLocationAvailableCapacity,
  PaymentGateway,
  PaymentMethod,
  PermissionGroup,
  Phone,
  Prepayment,
  Reminder,
  ReminderCreationRule,
  ReminderTemplate,
  ReportExport,
  RevisionLog,
  SearchResult,
  ScheduleReport,
  ShareReport,
  Shift,
  SidebarSection,
  SimpleBundle,
  Species,
  Speechmatics,
  StockItem,
  StockLocation,
  SummaryConsultation,
  SummaryDuration,
  SummaryRevenue,
  Support,
  Tag,
  TagText,
  Task,
  TreatmentDocument,
  UIConfig,
  UKPostcodeLookup,
  UKPostcodeResult,
  UnallocatedPayment,
  Upload,
  User,
  UserFilter,
  UserViewHistory,
  Ward,
  Weight,
  Wholesaler,
  ZendeskSettings,
}

/**
 * In case there is a model which Pascal case isn't correct for
 * the `model.modelName`, add an entry in the object below.
 *
 * The type for model must match the `components.schemas.[key]
 * defined in the `schema.yaml` file.
 */
const modelNameExceptions: { [k: string]: string } = {
  'conversation-message-media': 'ConversationMessageMedia',
  'message-receipt': 'ConversationMessageReceipt',
  participant: 'ConversationParticipant',
  'patient-cpr-status-entry': 'PatientCPRStatusEntry',
  'uk-postcode-lookup': 'UKPostcodeLookup',
  'uk-postcode-result': 'PostcodeResult',
}

const customKeyMappings: Record<string, string> = {
  patientLocation2: 'patient_location_2',
  reportingDimension1: 'reporting_dimension_1',
  reportingDimension2: 'reporting_dimension_2',
}

const transformKey = (key: string) => {
  return customKeyMappings[key] || _snakeCase(key)
}

const ApplicationSerializer = JSONAPISerializer.extend({
  alwaysIncludeLinkageData: true,
  keyForAttribute: transformKey,
  keyForRelationship: transformKey,
  typeKeyForModel(model: ModelInstance) {
    return (
      modelNameExceptions[model.modelName] ||
      _upperFirst(_camelCase(model.modelName))
    )
  },
})

export function createMockServer({ environment = 'test' } = {}) {
  const config = useRuntimeConfig()

  // See https://miragejs.com/docs/advanced/customizing-inflections/
  inflections('en', function (inflect) {
    inflect.irregular('zendeskSettings', 'zendeskSettings')
    inflect.irregular('mergedSettings', 'mergedSettings')
    inflect.irregular('onlineBookingSettings', 'onlineBookingSettings')
    inflect.irregular('conversationMessageMedia', 'conversationMessageMedia')
  })

  const server = createServer({
    environment,
    models: {
      accountNumber: AccountNumber.model,
      activePermissionsList: ActivePermissionsList.model,
      ai: AI.model,
      alternativePayer: AlternativePayer.model,
      alternativePrice: AlternativePrice.model,
      batch: Batch.model,
      billingNote: BillingNote.model,
      boardingEvent: BoardingEvent.model,
      breed: Breeds.model,
      bundle: Bundle.model,
      calendarAppointment: CalendarAppointment.model,
      calendarUser: CalendarUser.model,
      cancellationReason: CancellationReason.model,
      centralizedSetting: CentralizedSetting.model,
      client: Client.model,
      clientStatement: ClientStatement.model,
      communicationPreference: ClientCommunicationPreference.model,
      complaintType: ComplaintType.model,
      complaintTypeGroup: ComplaintTypeGroup.model,
      consultation: Consultation.model,
      consultationConversation: ConsultationConversation.model,
      consultationDiagnosis: ConsultationDiagnosis.model,
      consultationDischargeinstruction: ConsultationDischargeinstruction.model,
      consultationItemOverview: ConsultationItemOverview.model,
      consultationItemSubgroup: ConsultationItemSubgroup.model,
      consultationItemTemplate: ConsultationItemTemplate.model,
      consultationLaboratoryAnalysis: ConsultationLaboratoryAnalysis.model,
      consultationLaboratoryReferral: ConsultationLaboratoryReferral.model,
      consultationNote: ConsultationNote.model,
      consultationNoteVersion: ConsultationNoteVersions.model,
      consultationRefillOverview: ConsultationRefillOverview.model,
      consultationFood: ConsultationFood.model,
      consultationMedicine: ConsultationMedicine.model,
      consultationProcedure: ConsultationProcedure.model,
      consultationSupply: ConsultationSupply.model,
      treatmentDocument: TreatmentDocument.model,
      conversation: Conversation.model,
      conversationsSendMessageToClient: ConversationMessage.model,
      creditAllocation: CreditAllocation.model,
      customIntegration: CustomIntegration.model,
      dayNote: DayNote.model,
      department: Department.model,
      departmentGroup: DepartmentGroup.model,
      devPlaygroundPatientBilling: DevPlaygroundPatientBilling.model,
      devPlaygroundPatientBillingInvoice:
        DevPlaygroundPatientBillingInvoice.model,
      diagnosis: Diagnosis.model,
      dischargeInstructionVersion: DischargeInstructionVersions.model,
      estimate: Estimate.model,
      featureBlock: FeatureBlock.model,
      formAssignment: FormAssignment.model,
      formTemplate: FormTemplate.model,
      generalPrescription: GeneralPrescription.model,
      generalPrescriptionItem: GeneralPrescriptionItem.model,
      healthPlan: HealthPlan.model,
      homeDelivery: HomeDelivery.model,
      homeDeliveryOrder: HomeDeliveryOrder.model,
      importfile: ImportFile.model,
      insuranceClaim: InsuranceClaim.model,
      invoice: Invoice.model,
      invoiceEventLog: InvoiceEventLog.model,
      invoiceFinalization: InvoiceFinalization.model,
      invoicePayment: InvoicePayment.model,
      invoiceRow: InvoiceRow.model,
      italianMunicipality: ItalianMunicipality.model,
      catalogItemList: CatalogItemList.model,
      itemCategory: ItemCategory.model,
      itemDiagnosisGroup: ItemDiagnosis.model,
      itemSubgroup: ItemSubgroup.model,
      laboratoryGroup: LaboratoryGroup.model,
      listItem: ListItem.model,
      locumAssignment: LocumAssignment.model,
      locumUser: Locum.model,
      mergedSettings: MergedSettings.model,
      conversationMessageMedia: ConversationMessageMedia.model,
      message: Message.model,
      messageReceipt: MessageReceipt.model,
      microchipCheck: MicrochipCheck.model,
      navigationSearchResult: NavigationSearchResult.model,
      provetPayPaymentReport: ProvetPayPaymentReport.model,
      notification: Notification.model,
      onlineBookingSettings: OnlineBookingSettings.model,
      organization: Organization.model,
      organizationItem: OrganizationItem.model,
      participant: ConversationParticipant.model,
      providerMedicine: ProviderMedicine.model,
      patient: Patient.model,
      patientCprStatusEntry: PatientCPRStatusEntry.model,
      patientHealthPlan: PatientHealthPlan.model,
      patientHealthPlanItem: PatientHealthPlanItem.model,
      patientLocation: PatientLocation.model,
      patientLocationAttribute: PatientLocationAttribute.model,
      paymentMethod: PaymentMethod.model,
      permissionGroup: PermissionGroup.model,
      phone: Phone.model,
      prepayment: Prepayment.model,
      reminder: Reminder.model,
      reminderCreationRule: ReminderCreationRule.model,
      reminderTemplate: ReminderTemplate.model,
      refillItem: RefillItem.model,
      revisionLog: RevisionLog.model,
      scheduleReport: ScheduleReport.model,
      schedulingEvents: SchedulingEvent.model,
      searchResult: SearchResult.model,
      shareReport: ShareReport.model,
      sidebarSection: SidebarSection.model,
      simpleConsultationItem: SimpleConsultationItem.model,
      simpleBundle: SimpleBundle.model,
      species: Species.model,
      speechmatics: Speechmatics.model,
      stockItem: StockItem.model,
      stockLocation: StockLocation.model,
      summaryConsultation: SummaryConsultation.model,
      summaryDuration: SummaryDuration.model,
      summaryRevenue: SummaryRevenue.model,
      support: Support.model,
      tag: Tag.model,
      tagText: TagText.model,
      task: Task.model,
      taskRepeat: TaskRepeat.model,
      ukPostcodeLookup: UKPostcodeLookup.model,
      ukPostcodeResult: UKPostcodeResult.model,
      unallocatedPayment: UnallocatedPayment.model,
      upload: Upload.model,
      uploadGroup: UploadGroup.model,
      user: User.model,
      userFilter: UserFilter.model,
      userScheduling: Shift.model,
      userViewHistory: UserViewHistory.model,
      ward: Ward.model,
      weight: Weight.model,
      wholesaler: Wholesaler.model,
      zendeskSettings: ZendeskSettings.model,
    },

    factories: {
      accountNumber: AccountNumber.factory,
      activePermissionsList: ActivePermissionsList.factory,
      ai: AI.factory,
      alternativePayer: AlternativePayer.factory,
      alternativePrice: AlternativePrice.factory,
      batch: Batch.factory,
      billingNote: BillingNote.factory,
      boardingEvent: BoardingEvent.factory,
      breed: Breeds.factory,
      bundle: Bundle.factory,
      calendarAppointment: CalendarAppointment.factory,
      calendarUser: CalendarUser.factory,
      cancellationReason: CancellationReason.factory,
      centralizedSetting: CentralizedSetting.factory,
      client: Client.factory,
      clientStatement: ClientStatement.factory,
      communicationPreference: ClientCommunicationPreference.factory,
      complaintType: ComplaintType.factory,
      complaintTypeGroup: ComplaintTypeGroup.factory,
      consultation: Consultation.factory,
      consultationConversation: ConsultationConversation.factory,
      consultationDiagnosis: ConsultationDiagnosis.factory,
      consultationDischargeinstruction:
        ConsultationDischargeinstruction.factory,
      consultationItemOverview: ConsultationItemOverview.factory,
      consultationItemSubgroup: ConsultationItemSubgroup.factory,
      consultationItemTemplate: ConsultationItemTemplate.factory,
      consultationLaboratoryAnalysis: ConsultationLaboratoryAnalysis.factory,
      consultationLaboratoryReferral: ConsultationLaboratoryReferral.factory,
      consultationNote: ConsultationNote.factory,
      consultationNoteVersion: ConsultationNoteVersions.factory,
      consultationRefillOverview: ConsultationRefillOverview.factory,
      consultationFood: ConsultationFood.factory,
      consultationMedicine: ConsultationMedicine.factory,
      consultationProcedure: ConsultationProcedure.factory,
      consultationSupply: ConsultationSupply.factory,
      treatmentDocument: TreatmentDocument.factory,
      conversation: Conversation.factory,
      conversationsSendMessageToClient: ConversationMessage.factory,
      creditAllocation: CreditAllocation.factory,
      customIntegration: CustomIntegration.factory,
      dayNote: DayNote.factory,
      department: Department.factory,
      departmentGroup: DepartmentGroup.factory,
      devPlaygroundPatientBilling: DevPlaygroundPatientBilling.factory,
      devPlaygroundPatientBillingInvoice:
        DevPlaygroundPatientBillingInvoice.factory,
      diagnosis: Diagnosis.factory,
      dischargeInstructionVersion: DischargeInstructionVersions.factory,
      estimate: Estimate.factory,
      featureBlock: FeatureBlock.factory,
      formAssignment: FormAssignment.factory,
      formTemplate: FormTemplate.factory,
      generalPrescription: GeneralPrescription.factory,
      generalPrescriptionItem: GeneralPrescriptionItem.factory,
      healthPlan: HealthPlan.factory,
      homeDelivery: HomeDelivery.factory,
      homeDeliveryOrder: HomeDeliveryOrder.factory,
      importfile: ImportFile.factory,
      insuranceClaim: InsuranceClaim.factory,
      invoice: Invoice.factory,
      invoiceEventLog: InvoiceEventLog.factory,
      invoiceFinalization: InvoiceFinalization.factory,
      invoicePayment: InvoicePayment.factory,
      invoiceRow: InvoiceRow.factory,
      italianMunicipality: ItalianMunicipality.factory,
      catalogItemList: CatalogItemList.factory,
      itemCategory: ItemCategory.factory,
      itemDiagnosisGroup: ItemDiagnosis.factory,
      itemSubgroup: ItemSubgroup.factory,
      laboratoryGroup: LaboratoryGroup.factory,
      listItem: ListItem.factory,
      locumAssignment: LocumAssignment.factory,
      locumUser: Locum.factory,
      mergedSettings: MergedSettings.factory,
      conversationMessageMedia: ConversationMessageMedia.factory,
      message: Message.factory,
      messageReceipt: MessageReceipt.factory,
      microchipCheck: MicrochipCheck.factory,
      navigationSearchResult: NavigationSearchResult.factory,
      provetPayPaymentReport: ProvetPayPaymentReport.factory,
      notification: Notification.factory,
      onlineBookingSettings: OnlineBookingSettings.factory,
      organization: Organization.factory,
      organizationItem: OrganizationItem.factory,
      participant: ConversationParticipant.factory,
      providerMedicine: ProviderMedicine.factory,
      patient: Patient.factory,
      patientCprStatusEntry: PatientCPRStatusEntry.factory,
      patientHealthPlan: PatientHealthPlan.factory,
      patientHealthPlanItem: PatientHealthPlanItem.factory,
      patientLocation: PatientLocation.factory,
      patientLocationAttribute: PatientLocationAttribute.factory,
      paymentMethod: PaymentMethod.factory,
      permissionGroup: PermissionGroup.factory,
      phone: Phone.factory,
      prepayment: Prepayment.factory,
      reminder: Reminder.factory,
      reminderCreationRule: ReminderCreationRule.factory,
      reminderTemplate: ReminderTemplate.factory,
      refillItem: RefillItem.factory,
      revisionLog: RevisionLog.factory,
      scheduleReport: ScheduleReport.factory,
      schedulingEvents: SchedulingEvent.factory,
      searchResult: SearchResult.factory,
      shareReport: ShareReport.factory,
      simpleBundle: SimpleBundle.factory,
      simpleConsultationItem: SimpleConsultationItem.factory,
      species: Species.factory,
      speechmatics: Speechmatics.factory,
      stockItem: StockItem.factory,
      stockLocation: StockLocation.factory,
      summaryConsultation: SummaryConsultation.factory,
      summaryDuration: SummaryDuration.factory,
      summaryRevenue: SummaryRevenue.factory,
      support: Support.factory,
      tagText: TagText.factory,
      task: Task.factory,
      taskRepeat: TaskRepeat.factory,
      ukPostcodeLookup: UKPostcodeLookup.factory,
      ukPostcodeResult: UKPostcodeResult.factory,
      unallocatedPayment: UnallocatedPayment.factory,
      upload: Upload.factory,
      user: User.factory,
      userScheduling: Shift.factory,
      userViewHistory: UserViewHistory.factory,
      ward: Ward.factory,
      weight: Weight.factory,
      wholesaler: Wholesaler.factory,
      zendeskSettings: ZendeskSettings.factory,
    },

    serializers: {
      application: ApplicationSerializer,
      invoice: (
        ApplicationSerializer as unknown as typeof JSONAPISerializer
      ).extend({
        // Make relationships available for JSON:API includes
        alwaysIncludeLinkageData: true,
      }),
      unallocatedPayment: (
        ApplicationSerializer as unknown as typeof JSONAPISerializer
      ).extend({
        include: ['client', 'paymentMethod', 'department'],
      }),
    },

    fixtures: {
      sidebarSections: SidebarSection.fixtures,
    },

    seeds(server) {
      server.loadFixtures()

      // Create essential system data in advance
      Department.seeds(server)
      DepartmentGroup.seeds(server)
      MergedSettings.seeds(server)
      PaymentMethod.seeds(server)
      PermissionGroup.seeds(server)
      ActivePermissionsList.seeds(server)
      ConsultationItemSubgroup.seeds(server)

      server.create(
        'user',
        {
          email: 'ada@lovelace.com',
          first_name: 'Ada',
          initials: 'AL',
          last_name: 'Lovelace',
          work_role: 'VET',
        },
        'withActiveDepartment',
        'withHomeDepartment',
      )

      // Seed user filters after user is created
      UserFilter.seeds(server)
    },

    routes(this) {
      const server = this as MockServer

      // Uncomment temporarily to simulate slow API responses
      // this.timing = 2000

      this.urlPrefix = config.public.apiBaseUrl

      // The new countersale endpoint is not part of the API yet
      this.post('/:provet_id/countersale/new', () => ({
        redirect: `?mock-counter-sale=${getCurrentTimestamp()}`,
      }))

      // Pre-registration routes live at root (no /:provet_id/api namespace)
      PreregistrationContactMethods.routes(server)
      PreregistrationOTP.routes(server)

      // Set the namespace to the provet_id/api
      this.namespace = '/:provet_id/api'

      if (environment !== 'test') {
        /* -- LAZY SEEDING -- */
        // Extend the server's route handlers with lazy seeding.
        // With this, a new `seed` option is added to the GET, POST, PATCH, DELETE, and resource methods.
        // This option is used to seed the data for the route.
        const lazySeedingManager = useLazySeedingManager(server)
        Object.assign(server, {
          delete: lazySeedingManager.routeHandlers.delete,
          get: lazySeedingManager.routeHandlers.get,
          lazySeedingManager,
          patch: lazySeedingManager.routeHandlers.patch,
          post: lazySeedingManager.routeHandlers.post,
        })

        // Mark entities that were seeded during server initialization as already seeded
        // to prevent lazy seeding from seeding them again (causing duplicates)
        // Note: Only entities that have routes (in entitiesWithRoutes) need to be marked
        const entitiesSeededDuringInit: EntityWithRoutes[] = [
          'Department',
          'MergedSettings',
          'PaymentMethod',
          'PermissionGroup',
          'ActivePermissionsList',
          'UserFilter',
        ]
        entitiesSeededDuringInit.forEach((entity) => {
          lazySeedingManager.markAsSeeded(entity)
        })

        // Complete register of entities with routes.
        // These are registered through the `addRoutes` function which sets up auto-seeding
        // for all the routes in case LazySeeding is enabled. When disabled, routes are registered directly.
        // Register all the routes as normal when in test environment.
        for (const [entityName, module] of Object.entries(entitiesWithRoutes)) {
          if (!module.routes) {
            return
          }

          server.lazySeedingManager?.setAutoSeedingToRoutes(
            entityName as EntityWithRoutes,
            module,
            server,
          )
        }
      } else {
        // Register all the routes as normal when in test environment.
        for (const [_entityName, module] of Object.entries(
          entitiesWithRoutes,
        )) {
          module.routes(server)
        }
      }

      // Allow non-API requests to bypass MirageJS and reach the real network.
      // This ensures that requests to our API base URL (e.g., /api/*) are mocked,
      // while other requests (static assets, external APIs, etc.) pass through normally.
      this.passthrough((request) => {
        // Always pass through requests that don't start with apiBaseUrl
        if (!request.url.startsWith(config.public.apiBaseUrl)) {
          return true
        }

        const url = new URL(request.url)

        // When isFELogin is enabled (review-app workflow), the mock server is intentionally
        // bypassed for all API requests so they reach the real review-app backend.
        // The only exception is the navigation sidebar, which stays mocked so that
        // local feature-flag overrides (feature-flags-dev.json) control which nav items appear.
        if (config.public.isFELogin) {
          const isNavigationSidebar = url.pathname.includes(
            '/api/navigation/sidebar',
          )
          // Return true to passthrough, false to let Mirage handle it
          return !isNavigationSidebar
        }

        return false
      })

      // Change logging to debug/verbose level for passthrough requests
      // See https://miragejs.com/api/classes/server/#logging & https://github.com/pretenderjs/pretender#pass-through-requests
      this.pretender.passthroughRequest = function (verb, path, request) {
        console.debug(
          `Mirage: Passthrough request for ${verb} ${request.responseURL}`,
        )
      }
    },
  })

  // Override Mirage's nested console.groupCollapsed logging with a single
  // group level to work around Chrome DevTools bug where nested groups render empty
  server.pretender.handledRequest = function (verb, _path, request) {
    // Pretender's FakeXMLHttpRequest has `url` at runtime but not in its type definitions
    const requestUrl = (request as unknown as { url: string }).url

    let loggedResponse, loggedRequest
    try {
      loggedResponse = JSON.parse(request.responseText)
    } catch {
      loggedResponse = request.responseText
    }
    try {
      loggedRequest = JSON.parse(request.requestBody)
    } catch {
      loggedRequest = request.requestBody
    }

    const shortPath = '/api' + new URL(requestUrl).pathname.split('/api').pop()
    mirageLog(verb.toUpperCase(), Number(request.status), shortPath)
    console.log(
      '%cURL:%c ' + requestUrl,
      'font-weight: bold',
      'font-weight: normal',
    )
    const labelStyle = 'font-weight: bold'
    console.log('%cResponse:', labelStyle, loggedResponse)
    const queryParams = Object.fromEntries(new URL(requestUrl).searchParams)
    console.log('%cRequest (data):', labelStyle, {
      body: loggedRequest,
      params: request.params,
      queryParams,
    })
    console.log('%cRequest (raw):', labelStyle, request)
    console.groupEnd()
  }

  return server
}

// Enhanced HTTP method type that adds seeding support
type RouteHandlerWithSeeding<T extends 'get' | 'post' | 'patch' | 'delete'> =
  ReturnType<typeof createMockServer>[T] & {
    (
      path: string,
      handler?: Parameters<ReturnType<typeof createMockServer>[T]>[1],
      options?: ExtendedHandlerOptions,
    ): ReturnType<ReturnType<typeof createMockServer>[T]>
  }

export type MockServer = ReturnType<typeof createMockServer> & {
  delete: RouteHandlerWithSeeding<'delete'>
  // Enhanced HTTP methods that support seeding via options.seed
  get: RouteHandlerWithSeeding<'get'>
  lazySeedingManager?: ReturnType<typeof useLazySeedingManager>
  patch: RouteHandlerWithSeeding<'patch'>
  post: RouteHandlerWithSeeding<'post'>
}

export type BaseResponse<T = unknown, U = unknown> = {
  id?: string
  type?: string
  attributes?: Record<string, unknown>
  data: T[]
  included?: U[]
  jsonapi?: Record<string, unknown>
  links?: Record<string, unknown>
  meta?: Record<string, unknown>
}

export type MirageServer<T = BaseResponse> = MockServer & {
  serialize(data: Collection<unknown> | ModelInstance | BaseResponse): T
}
